import { useMemo } from 'react';

import { useQueries, useQuery } from '@tanstack/react-query';

import {
  getAgentsInterestedList,
  getMyAgent,
} from '../accounts/AccountService';
import { searchPolicies } from '../api/policies.api';
import { searchQuotes } from '../api/quotes.api';
import { getAgencyLocations, type AgencyLocationDto } from './api/myAgent.api';
import { isChangeMeAgency } from './changeMeAgency';
import {
  canSearchForAgent,
  resolveAgentSource,
  type ResolvedAgents,
} from './resolveAgentSource';

const MY_AGENT_KEY = 'my-agent';
const SOURCE_PAGE_SIZE = 25;

interface InterestedAgency {
  readonly id?: string;
  readonly name?: string;
  readonly contactName?: string;
  readonly contactEmail?: string;
  readonly contactPhone?: string;
  readonly city?: string;
  readonly state?: string;
}

/**
 * A row from either search. Which status key is populated depends on which
 * endpoint produced it — policies carry `status`, quotes carry `agencyStatus`
 * and `customerStatus` — so all three are declared here rather than the
 * `status` alone this used to claim.
 */
interface SourceRow {
  readonly agencyId?: string;
  readonly agencyName?: string;
  readonly agentFirstName?: string;
  readonly agentLastName?: string;
  readonly agentEmail?: string;
  readonly agentPhone?: string;
  readonly status?: string;
  readonly agencyStatus?: string;
  readonly customerStatus?: string;
  readonly effectiveDate?: string;
}

export interface UseResolvedAgentResult {
  readonly agent: ResolvedAgents;
  readonly location?: AgencyLocationDto;
  readonly interested: readonly InterestedAgency[];
  /** Whether the agent search is offered. See `canSearchForAgent`. */
  readonly canSearch: boolean;
  readonly isLoading: boolean;
  readonly isError: boolean;
}

/**
 * Newest effective date first. The search endpoints return whatever order
 * Elasticsearch gives, so without this a policyholder holding two in-force
 * policies on different agencies would see a non-deterministic agent.
 */
const byNewestEffectiveDate = (rows: readonly SourceRow[]): SourceRow[] =>
  [...rows].sort((a, z) => {
    const left = a.effectiveDate ?? '';
    const right = z.effectiveDate ?? '';
    return right.localeCompare(left);
  });

/**
 * Field-by-field, hard link first: an AgencyDto that carries only a name must
 * not blank out an address the directory did return, and AgencyDto never carries
 * a geo point so that always comes from the directory.
 */
const mergeAgencyLocation = (
  fromHardLink?: Partial<AgencyLocationDto>,
  fromDirectory?: AgencyLocationDto
): AgencyLocationDto | undefined => {
  if (!fromHardLink) {
    return fromDirectory;
  }
  if (!fromDirectory) {
    return fromHardLink as AgencyLocationDto;
  }
  return {
    ...fromDirectory,
    ...Object.fromEntries(
      Object.entries(fromHardLink).filter(([, value]) => !!value)
    ),
    location: fromHardLink.location ?? fromDirectory.location,
  } as AgencyLocationDto;
};

export function useResolvedAgent(): UseResolvedAgentResult {
  const sources = useQueries({
    queries: [
      {
        queryKey: [MY_AGENT_KEY, 'policies'],
        queryFn: () => searchPolicies({ params: { size: SOURCE_PAGE_SIZE } }),
        retry: false,
      },
      {
        queryKey: [MY_AGENT_KEY, 'quotes'],
        queryFn: () => searchQuotes({ params: { size: SOURCE_PAGE_SIZE } }),
        retry: false,
      },
      {
        queryKey: [MY_AGENT_KEY, 'hard-link'],
        queryFn: () => getMyAgent(),
        retry: false,
      },
      {
        queryKey: [MY_AGENT_KEY, 'interested'],
        queryFn: () => getAgentsInterestedList(),
        retry: false,
      },
    ],
  });

  const [policiesQ, quotesQ, hardLinkQ, interestedQ] = sources;

  const hardLink: any = (hardLinkQ.data as any)?.data?.[0] ?? null;

  const policyData = (policiesQ.data as any)?.data?.content;
  const quoteData = (quotesQ.data as any)?.data?.content;
  const interestedData = (interestedQ.data as any)?.data?.content;

  const policyRows = useMemo(
    () => byNewestEffectiveDate(policyData ?? []),
    [policyData]
  );
  const quoteRows = useMemo(
    () => byNewestEffectiveDate(quoteData ?? []),
    [quoteData]
  );

  // A binder is not "a quote in BOUND status", which is what this hook used to
  // assume: it comes back from the policy search, which is why the
  // policyholder listing renders one "Binders/Policies" section from a single
  // searchPolicies call. The quote rows stay in because a BOUND quote row must
  // resolve as a binder too — demoted to a plain quote, a hard-linked agency
  // would outrank it and push the real binder into the secondary card.
  //
  // Sorted as one list rather than concatenating two already-sorted ones, or
  // every policy row would outrank a newer quote row by position alone.
  const binderRows = useMemo(
    () => byNewestEffectiveDate([...(policyData ?? []), ...(quoteData ?? [])]),
    [policyData, quoteData]
  );

  // Rows arrive with either contact* or agent* fields; the table and the map
  // popover read contact*, so an agent*-shaped row rendered blank cells. The
  // page this replaced normalised these in an effect.
  const interested: InterestedAgency[] = useMemo(
    () =>
      (interestedData ?? [])
        // The "Change Me" holding agency is where unmatched submissions are
        // parked, so it lands in this list without anyone having expressed
        // interest. Dropping it here keeps it out of both the table and the
        // last-resort agent resolution below.
        .filter((row: any) => !isChangeMeAgency(row))
        .map((row: any) => ({
          ...row,
          contactName: row?.contactName || row?.agentName,
          contactEmail: row?.contactEmail || row?.agentEmail,
          contactPhone: row?.contactPhone || row?.agentPhone,
        })),
    [interestedData]
  );

  // Memoised so consumers can depend on its identity: this drives
  // contactsByAgencyId in the page, which would otherwise be rebuilt on every
  // render and hand FindMyAgent a fresh object each time.
  const agent = useMemo(
    () =>
      resolveAgentSource({
        policies: policyRows,
        binders: binderRows,
        quotes: quoteRows,
        hardLink,
        interested,
      }),
    [policyRows, binderRows, quoteRows, hardLink, interested]
  );

  // Closed until both sources answer: a failed or pending policy or hard-link
  // lookup cannot show that the account is free to choose a new agent.
  const canSearch =
    policiesQ.isSuccess &&
    hardLinkQ.isSuccess &&
    canSearchForAgent({ policies: policyRows, hardLink });

  const agencyId = agent.primary.agencyId;

  // The hard-link endpoint returns the agency itself — AgencyDto carries
  // address1/2/3, city, state and zipCode — so it can answer the address without
  // the directory, which ignores its agencyId filter.
  //
  // Only when it describes the agency we actually resolved: a live policy or
  // binder from a different agency must not borrow the hard link's address.
  const hardLinkMatchesAgent =
    !!hardLink && (!agencyId || hardLink.agencyId === agencyId);
  const hardLinkAddress = hardLinkMatchesAgent
    ? {
        id: hardLink.agencyId ?? 'hard-link',
        agencyId: hardLink.agencyId,
        name: hardLink.name,
        address1: hardLink.address1,
        address2: hardLink.address2,
        city: hardLink.city,
        state: hardLink.state,
        zipCode: hardLink.zipCode,
        country: hardLink.country,
        phoneNumber: hardLink.contactPhone,
        businessEmail: hardLink.contactEmail,
      }
    : undefined;

  const locationQ = useQuery({
    queryKey: [MY_AGENT_KEY, 'location', agencyId],
    queryFn: () => getAgencyLocations({ agencyId: agencyId as string }),
    enabled: !!agencyId,
  });

  return {
    agent,
    // Address from the hard link when it has one, coordinates from the directory
    // when it can supply them: AgencyDto has no geo point, and the directory's
    // address is unreliable until its agencyId filter lands.
    location: mergeAgencyLocation(
      hardLinkAddress,
      (locationQ.data as any)?.data?.content?.[0]
    ),
    interested,
    canSearch,
    isLoading: sources.some((query) => query.isLoading),
    // Only a wholesale failure is worth an error banner. One dead source still
    // leaves the others able to resolve an agent — notably /agency/myagent, which
    // 403s until its backend gate is deployed.
    isError: sources.every((query) => query.isError),
  };
}
